'use client'
import React, { useState, useMemo } from 'react'
import { Pencil } from 'lucide-react'
import { Button, Form } from 'react-bootstrap'

export interface Column<T> {
    key: keyof T;
    header: React.ReactNode;
    className?: string;
    headerClassName?: string;
    cellClassName?: string;
    style?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    cellStyle?: React.CSSProperties;
    render?: (value: any, item: T) => React.ReactNode;
    disableSort?: boolean;
}

interface TablaGenericaProps<T> {
    data: T[]
    columns: Column<T>[]
    isDarkMode?: boolean
    onEdit?: (item: T) => void
    onSortChange?: (column: keyof T, direction: 'asc' | 'desc') => void
    sortColumn?: keyof T | null
    sortDirection?: 'asc' | 'desc'
    // Props para selección
    seleccionable?: boolean
    filasSeleccionadas?: string[]
    onSeleccionarFila?: (index: number) => void
    onSeleccionarTodos?: (e: React.ChangeEvent<HTMLInputElement>) => void
    indiceInicio?: number
    // Función para obtener el ID de un item (necesario para selección)
    obtenerIdItem?: (item: T) => string
}

export function TablaGenerica<T extends Record<string, any>>({
    data,
    columns,
    isDarkMode = false,
    onEdit,
    onSortChange,
    sortColumn: externalSortColumn,
    sortDirection: externalSortDirection,
    // Props para selección
    seleccionable = false,
    filasSeleccionadas = [],
    onSeleccionarFila,
    onSeleccionarTodos,
    indiceInicio = 0,
    obtenerIdItem
}: TablaGenericaProps<T>) {

    const [internalSortColumn, setInternalSortColumn] = useState<keyof T | null>(null)
    const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>('asc')

    const sortColumn = externalSortColumn !== undefined ? externalSortColumn : internalSortColumn;
    const sortDirection = externalSortDirection !== undefined ? externalSortDirection : internalSortDirection;

    const sortedData = useMemo(() => {
        if (!sortColumn) return data

        return [...data].sort((a, b) => {
            const aValue = a[sortColumn]
            const bValue = b[sortColumn]

            if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
                return sortDirection === 'asc'
                    ? Number(aValue) - Number(bValue)
                    : Number(bValue) - Number(aValue)
            }

            return sortDirection === 'asc'
                ? aValue?.toString().localeCompare(bValue?.toString())
                : bValue?.toString().localeCompare(aValue?.toString())
        })
    }, [data, sortColumn, sortDirection])

    const handleSort = (key: keyof T) => {
        let newDirection: 'asc' | 'desc' = 'asc';

        if (sortColumn === key) {
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        }

        if (onSortChange) {
            onSortChange(key, newDirection);
        } else {
            setInternalSortColumn(key);
            setInternalSortDirection(newDirection);
        }
    }

    // Función para obtener el indicador de ordenamiento
    const getSortIndicator = (col: Column<T>) => {
        if (col.disableSort) {
            return null;
        }

        if (sortColumn === col.key) {
            return (
                <span className="ms-1 text-danger" style={{ fontSize: '0.9em' }}>
                    {sortDirection === 'asc' ? '▲' : '▼'}
                </span>
            );
        }

        return (
            <span
                className="ms-1"
                style={{
                    opacity: 0.3,
                    fontSize: '0.8em',
                    display: 'inline-block'
                }}
            >
                ↕
            </span>
        );
    };

    // Verificar si todas las filas de la página están seleccionadas
    const todasSeleccionadas = seleccionable &&
        data.length > 0 &&
        obtenerIdItem &&
        data.every((item) => {
            const id = obtenerIdItem(item);
            return filasSeleccionadas.includes(id);
        });

    return (
        <div className="table-responsive">
            <table className={`table table-sm super-small ${isDarkMode ? "table-dark" : "table-hover table-striped"}`}>
                <thead>
                    <tr>
                        {seleccionable && (
                            <th style={{ width: "40px" }} className="text-center">
                                {onSeleccionarTodos && (
                                    <Form.Check
                                        type="checkbox"
                                        onChange={onSeleccionarTodos}
                                        checked={todasSeleccionadas || false}
                                    />
                                )}
                            </th>
                        )}

                        {columns.map((col, i) => (
                            <th
                                key={i}
                                style={{
                                    ...(col.headerStyle || col.style || {}),
                                    cursor: col.disableSort ? "default" : "pointer",
                                    userSelect: "none",
                                }}
                                className={`text-nowrap small ${col.headerClassName || col.className || ''}`}
                                onClick={() => !col.disableSort && handleSort(col.key)}
                                title={col.disableSort ? "" : "Haz clic para ordenar"}
                            >
                                {col.header}
                                {getSortIndicator(col)}
                            </th>
                        ))}

                        {onEdit && (
                            <th style={{ width: "90px" }} className="text-center">
                                Acción
                            </th>
                        )}
                    </tr>
                </thead>

                <tbody>
                    {sortedData.length === 0 ? (
                        <tr>
                            <td
                                colSpan={
                                    columns.length +
                                    (onEdit ? 1 : 0) +
                                    (seleccionable ? 1 : 0)
                                }
                                className="text-center text-muted"
                            >
                                Sin registros
                            </td>
                        </tr>
                    ) : (
                        sortedData.map((item, index) => {
                            const indexReal = indiceInicio + index;
                            const id = obtenerIdItem ? obtenerIdItem(item) : indexReal.toString();

                            return (
                                <tr key={index}>
                                    {seleccionable && (
                                        <td style={{ width: "40px" }} className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                onChange={() => onSeleccionarFila?.(indexReal)}
                                                checked={filasSeleccionadas.includes(id)}
                                            />
                                        </td>
                                    )}

                                    {columns.map((col, i) => (
                                        <td
                                            key={i}
                                            className={`small ${col.cellClassName || ''}`}
                                            style={col.cellStyle || col.style || {}}
                                        >
                                            {col.render
                                                ? col.render(item[col.key], item)
                                                : item[col.key]}
                                        </td>
                                    ))}

                                    {onEdit && (
                                        <td className="text-center text-nowrap">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => onEdit(item)}
                                            >
                                                Editar <Pencil size={14} />
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    )
}